/**
 * AusschreibungScreen - the full-screen Placement (Ausschreibung) detail (P2 Placement Slice 4).
 * Route: /ausschreibungen/$id (?tab= selects the active tab).
 *
 * The 6-tab shell (§7) is final from Slice 4: Risikoprofil · Soll-Deckung · Offertanfragen ·
 * Offerten · Underwriting · Historie - every body is a <PlacementTabPlaceholder> this slice, and
 * each later slice (5-15) replaces exactly ONE `case` without reshaping the shell. The header
 * carries the status badge (§10.1), the closure badge (§10.6) and the meta row (with the empty
 * Slice-7 carrier-chip slot); the only live action is «Placement abbrechen» (entwurf|entschieden).
 *
 * [Source: apps/web/src/features/entity-detail/PoliceDetailScreen.tsx] (DetailShell + ?tab= seed idiom)
 */

import { useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ClipboardList, XCircle } from 'lucide-react';
import { DetailShell } from '../entity-detail/components/DetailShell';
import { EntityLink } from '../../components/ui/EntityLink';
import { Button } from '../../components/ui/Button';
import { Dialog } from '../administration/components';
import { useEffectivePermissions } from '../administration/useEffectivePermissions';
import { dateShortDe } from '../../lib/format';
import {
  PLACEMENT_ANLASS_LABELS,
  PLACEMENT_STATUS,
  PLACEMENT_TAB,
  PLACEMENT_TAB_SLICE,
  PLACEMENT_TAB_VALUES,
  PK_WARTET_AKTIVIERUNG_HINWEIS,
  buildPkRueckmeldungText,
  canTransitionPlacement,
} from '@brokly/shared';
import type { PlacementTab } from '@brokly/shared';
import { PolicenkontrolleBefundCard } from './PolicenkontrolleBefundCard';
import { PkRueckmeldungDialog } from './PkRueckmeldungDialog';
import { useRueckmeldungVersichererAbschluss } from './usePlacementAbschluss';
import { usePlacement, useAbbrechenPlacement } from './usePlacements';
import { useAbschliessenRisikoprofil, useAbschliessenSollDeckung } from './usePlacementSnapshot';
import { PlacementStatusBadge } from './PlacementStatusBadge';
import { PlacementClosureBadge } from './PlacementClosureBadge';
import { PlacementCarrierChips } from './PlacementCarrierChips';
import { OffertenTab } from './OffertenTab';
import { UnderwritingTab } from './UnderwritingTab';
import { usePlacementAntrag } from './usePlacementAntrag';
import { usePlacementUnderwriting } from './usePlacementUnderwriting';
import { usePlacementAbschluss } from './usePlacementAbschluss';
import { HistorieTab } from './HistorieTab';
import { RisikoprofilTab } from './RisikoprofilTab';
import { SollDeckungTab } from './SollDeckungTab';
import { SubmissionsTab } from './SubmissionsTab';
import { usePlacementVersichererAuswahl } from './usePlacementVersicherer';
import { usePlacementSubmissions, useErstellenSubmissions } from './usePlacementSubmissions';
import { usePlacementKundenfreigabe } from './usePlacementKundenfreigabe';
import type { PlacementDetail } from '@brokly/shared';

function isPlacementTab(v: string | undefined): v is PlacementTab {
  return v !== undefined && (PLACEMENT_TAB_VALUES as readonly string[]).includes(v);
}

/** Map a known abschliessen error code (from the API message) to a German-safe banner line. */
function mapAbschliessenError(message: string): string {
  if (message.includes('answers_open')) return 'Es sind noch offene Vorgaben. Bitte alle nicht-optionalen Vorgaben beantworten.';
  if (message.includes('placement_locked')) return 'Die Ausschreibung ist gesperrt und kann nicht mehr bearbeitet werden.';
  if (message.includes('invalid_transition')) return 'Dieser Schritt ist im aktuellen Status nicht moeglich.';
  return message || 'Abschliessen fehlgeschlagen.';
}

/** Map a known «Offertanfragen erstellen» error code to a German-safe banner line. */
function mapErstellenError(message: string): string {
  if (message.includes('placement_locked')) return 'Die Ausschreibung ist gesperrt und kann nicht mehr bearbeitet werden.';
  if (message.includes('selection_conflict')) return 'Die Versicherer-Auswahl hat sich geaendert. Bitte neu laden und erneut versuchen.';
  if (message.includes('vorgaben_nicht_erfasst')) return 'Zuerst Risikoprofil und Soll-Deckung abschliessen.';
  if (message.includes('invalid_transition')) return 'Dieser Schritt ist im aktuellen Status nicht moeglich.';
  return message || 'Offertanfragen erstellen fehlgeschlagen.';
}

/**
 * The tab-body switch — one `case` per PLACEMENT_TAB value, in PLACEMENT_TAB_VALUES order. Slice 5
 * fills the Risikoprofil + Soll-Deckung cases; the other four stay placeholders until their slice.
 */
function renderTabBody(
  activeTab: PlacementTab,
  p: PlacementDetail,
  canWrite: boolean,
  canSend: boolean,
  submissions: { versendenOpen: boolean; onVersendenClose: () => void },
  kundenfreigabe: { openKind: 'zustellen' | 'entscheid' | null; onOpenHandled: () => void },
  antrag: { openKind: 'vorbereiten' | 'signatur' | 'einreichen' | null; onOpenHandled: () => void },
  uw: { openKind: 'annehmen' | 'ablehnen' | null; onOpenHandled: () => void; onAntragVorbereiten: () => void },
) {
  switch (activeTab) {
    case PLACEMENT_TAB.RISIKOPROFIL:
      return <RisikoprofilTab placementId={p.id} subjectId={p.subject_id} subjectType={p.subject_type} canWrite={canWrite} />;
    case PLACEMENT_TAB.SOLL_DECKUNG:
      return <SollDeckungTab placementId={p.id} canWrite={canWrite} />;
    case PLACEMENT_TAB.SUBMISSIONS:
      return (
        <SubmissionsTab
          placementId={p.id}
          canWrite={canWrite}
          versendenOpen={submissions.versendenOpen}
          onVersendenClose={submissions.onVersendenClose}
        />
      );
    case PLACEMENT_TAB.OFFERTEN:
      return (
        <OffertenTab
          placementId={p.id}
          canWrite={canWrite}
          kundenfreigabeOpenKind={kundenfreigabe.openKind}
          onKundenfreigabeOpenHandled={kundenfreigabe.onOpenHandled}
        />
      );
    case PLACEMENT_TAB.UNDERWRITING:
      return (
        <UnderwritingTab
          placementId={p.id}
          canWrite={canWrite}
          openKind={antrag.openKind}
          onOpenHandled={antrag.onOpenHandled}
          uwOpenKind={uw.openKind}
          onUwOpenHandled={uw.onOpenHandled}
          onAntragVorbereiten={uw.onAntragVorbereiten}
        />
      );
    case PLACEMENT_TAB.HISTORIE:
      return <HistorieTab placementId={p.id} canWrite={canWrite} canSend={canSend} />;
  }
}

export function AusschreibungScreen() {
  const { id } = useParams({ strict: false }) as { id: string };
  const search = useSearch({ strict: false }) as { tab?: string };
  const navigate = useNavigate();
  const { can } = useEffectivePermissions();

  const query = usePlacement(id);
  // Slice 7: the header Versicherer-chips (ausgewaehlt + inaktiv), visible on every tab.
  const auswahl = usePlacementVersichererAuswahl(id);
  // Slice 8: submissions drive the overlaid chips + the Tab-3b primary CTA (erstellen / versenden).
  const submissions = usePlacementSubmissions(id);
  const erstellenSubmissions = useErstellenSubmissions(id);
  const abbrechen = useAbbrechenPlacement(id);
  const abschliessenRp = useAbschliessenRisikoprofil(id);
  const abschliessenSoll = useAbschliessenSollDeckung(id);
  // Slice 12: the Tab-4c Kundenfreigabe facts drive the `empfohlen` primary CTA (only queried then).
  const kf = usePlacementKundenfreigabe(id, query.data?.status === PLACEMENT_STATUS.EMPFOHLEN);
  // Slice 13: the Tab-5a Antrag facts drive the `entschieden` primary CTA (only queried then).
  const ant = usePlacementAntrag(id, query.data?.status === PLACEMENT_STATUS.ENTSCHIEDEN);
  // Slice 14: the Tab-5b Underwriting facts drive the `entschieden` Rueckmeldung / Kundenentscheid CTA.
  const uw = usePlacementUnderwriting(id, query.data?.status === PLACEMENT_STATUS.ENTSCHIEDEN);
  // Slice 15: the Tab-6 Abschluss facts drive the `entschieden` «Abweichung klaeren» + `abgeschlossen`
  // «Police zustellen» primaries (queried while entschieden or abgeschlossen).
  const ab = usePlacementAbschluss(
    id,
    query.data?.status === PLACEMENT_STATUS.ENTSCHIEDEN || query.data?.status === PLACEMENT_STATUS.ABGESCHLOSSEN,
  );

  const [activeTab, setActiveTab] = useState<PlacementTab>(
    isPlacementTab(search.tab) ? search.tab : PLACEMENT_TAB_VALUES[0],
  );
  const [abbrechenOpen, setAbbrechenOpen] = useState(false);
  const [versendenOpen, setVersendenOpen] = useState(false);
  const [kfOpenKind, setKfOpenKind] = useState<'zustellen' | 'entscheid' | null>(null);
  const [antragOpenKind, setAntragOpenKind] = useState<'vorbereiten' | 'signatur' | 'einreichen' | null>(null);
  const [uwOpenKind, setUwOpenKind] = useState<'annehmen' | 'ablehnen' | null>(null);
  const [grund, setGrund] = useState('');
  const [abbrechenError, setAbbrechenError] = useState<string | null>(null);
  const [primaryError, setPrimaryError] = useState<string | null>(null);
  const [pkRueckmeldenOpen, setPkRueckmeldenOpen] = useState(false);
  const pkRueckmelden = useRueckmeldungVersichererAbschluss(id);

  if (query.isLoading) {
    return <p className="p-4 text-sm text-muted-foreground">Lade Ausschreibung…</p>;
  }
  if (query.isError || !query.data) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 p-12 text-center">
        <p className="text-sm text-muted-foreground" data-testid="aus-notfound">
          Ausschreibung nicht gefunden.
        </p>
      </div>
    );
  }

  const p = query.data;
  const backToKunde = () =>
    void navigate({ to: '/kunden/$id', params: { id: p.subject_id }, search: { tab: 'ausschreibungen' } });

  const onTabChange = (value: string) => {
    if (!isPlacementTab(value)) return;
    setActiveTab(value);
    void navigate({ to: '/ausschreibungen/$id', params: { id }, search: { tab: value }, replace: true });
  };

  const canWrite = can('placement.write');
  const canSendMail = can('communication.send');
  const canAbbrechen = canWrite && canTransitionPlacement(p.status, PLACEMENT_STATUS.ABGEBROCHEN);

  const tabs = PLACEMENT_TAB_VALUES.map((t) => ({ value: t, label: PLACEMENT_TAB_SLICE[t].label }));

  // primaryAction (Slice 5): Tab-1 abschliessen (entwurf) / Tab-2 abschliessen (risikoprofil_erfasst).
  async function runAbschliessen(which: 'risikoprofil' | 'soll') {
    setPrimaryError(null);
    try {
      if (which === 'risikoprofil') await abschliessenRp.mutateAsync();
      else await abschliessenSoll.mutateAsync();
    } catch (err) {
      setPrimaryError(mapAbschliessenError(err instanceof Error ? err.message : ''));
    }
  }
  const primaryPending = abschliessenRp.isPending || abschliessenSoll.isPending;
  let primaryAction: ReactNode = undefined;
  if (canWrite && p.status === PLACEMENT_STATUS.ENTWURF) {
    primaryAction = (
      <Button onClick={() => void runAbschliessen('risikoprofil')} disabled={primaryPending} data-testid="aus-primary-risikoprofil">
        {abschliessenRp.isPending ? 'Wird abgeschlossen …' : 'Risikoprofil abschliessen'}
      </Button>
    );
  } else if (canWrite && p.status === PLACEMENT_STATUS.RISIKOPROFIL_ERFASST) {
    primaryAction = (
      <Button onClick={() => void runAbschliessen('soll')} disabled={primaryPending} data-testid="aus-primary-soll">
        {abschliessenSoll.isPending ? 'Wird abgeschlossen …' : 'Soll-Deckung abschliessen'}
      </Button>
    );
  } else if (
    canWrite &&
    (p.status === PLACEMENT_STATUS.VORGABEN_ERFASST || p.status === PLACEMENT_STATUS.AUSGESCHRIEBEN)
  ) {
    // Slice 8 Tab-3b primary CTA: send the neu Offertanfragen (needs communication.send), else
    // create them from the selection.
    const sub = submissions.data;
    const canSend = canWrite && can('communication.send');
    if (sub && sub.neu > 0 && canSend) {
      primaryAction = (
        <Button
          onClick={() => {
            setVersendenOpen(true);
            if (activeTab !== PLACEMENT_TAB.SUBMISSIONS) onTabChange(PLACEMENT_TAB.SUBMISSIONS);
          }}
          data-testid="aus-primary-versenden"
        >
          Offertanfragen versenden ({sub.neu})
        </Button>
      );
    } else if (sub && sub.erstellbar > 0) {
      primaryAction = (
        <Button
          onClick={() => {
            setPrimaryError(null);
            void erstellenSubmissions
              .mutateAsync()
              .catch((err: unknown) => setPrimaryError(mapErstellenError(err instanceof Error ? err.message : '')));
            if (activeTab !== PLACEMENT_TAB.SUBMISSIONS) onTabChange(PLACEMENT_TAB.SUBMISSIONS);
          }}
          disabled={erstellenSubmissions.isPending}
          data-testid="aus-primary-erstellen"
        >
          Offertanfragen erstellen ({sub.erstellbar})
        </Button>
      );
    }
  } else if (canWrite && p.status === PLACEMENT_STATUS.EMPFOHLEN) {
    // Slice 12 Tab-4c primary CTA: deliver the released comparison, else record the decision.
    if (kf.data?.zustellen_erlaubt) {
      primaryAction = (
        <Button
          onClick={() => {
            setKfOpenKind('zustellen');
            if (activeTab !== PLACEMENT_TAB.OFFERTEN) onTabChange(PLACEMENT_TAB.OFFERTEN);
          }}
          data-testid="aus-primary-zustellen"
        >
          Dem Kunden zustellen
        </Button>
      );
    } else if (kf.data?.entscheid_erfassen_erlaubt) {
      primaryAction = (
        <Button
          onClick={() => {
            setKfOpenKind('entscheid');
            if (activeTab !== PLACEMENT_TAB.OFFERTEN) onTabChange(PLACEMENT_TAB.OFFERTEN);
          }}
          data-testid="aus-primary-entscheid"
        >
          Kundenentscheid erfassen
        </Button>
      );
    }
  } else if (canWrite && p.status === PLACEMENT_STATUS.ENTSCHIEDEN) {
    // Slice 13 Tab-5a primary CTA: prepare the Antrag, else send it for signature, else einreichen.
    const openAntrag = (kind: 'vorbereiten' | 'signatur' | 'einreichen') => {
      setAntragOpenKind(kind);
      if (activeTab !== PLACEMENT_TAB.UNDERWRITING) onTabChange(PLACEMENT_TAB.UNDERWRITING);
    };
    const openUw = (kind: 'annehmen' | 'ablehnen') => {
      setUwOpenKind(kind);
      if (activeTab !== PLACEMENT_TAB.UNDERWRITING) onTabChange(PLACEMENT_TAB.UNDERWRITING);
    };
    if (ant.data?.vorbereiten_erlaubt) {
      primaryAction = (
        <Button onClick={() => openAntrag('vorbereiten')} data-testid="aus-primary-antrag">
          Antrag vorbereiten
        </Button>
      );
    } else if (ant.data?.aktionen.signatur_senden) {
      primaryAction = (
        <Button onClick={() => openAntrag('signatur')} data-testid="aus-primary-signatur">
          Zur Unterschrift senden
        </Button>
      );
    } else if (ant.data?.aktionen.einreichen) {
      primaryAction = (
        <Button onClick={() => openAntrag('einreichen')} data-testid="aus-primary-einreichen">
          Antrag einreichen
        </Button>
      );
    } else if (uw.data?.aktionen.annehmen && uw.data.carrier_reply?.task_open) {
      primaryAction = (
        <Button onClick={() => openUw('annehmen')} data-testid="aus-primary-rueckmeldung">
          Rueckmeldung erfassen
        </Button>
      );
    } else if (uw.data?.aktionen.runde_entscheid_erfassen) {
      primaryAction = (
        <Button onClick={() => { if (activeTab !== PLACEMENT_TAB.UNDERWRITING) onTabChange(PLACEMENT_TAB.UNDERWRITING); }} data-testid="aus-primary-uw-runde">
          Kundenentscheid erfassen
        </Button>
      );
    } else if (uw.data?.aktionen.antrag_vorbereiten) {
      primaryAction = (
        <Button onClick={() => openAntrag('vorbereiten')} data-testid="aus-primary-uw-vorbereiten">
          Neuen Antrag vorbereiten
        </Button>
      );
    } else if (ab.data?.aktionen.abweichung_klaeren) {
      // Slice 15: an open Abweichung from the Policenkontrolle needs the broker's «Abweichung klaeren».
      primaryAction = (
        <Button onClick={() => { if (activeTab !== PLACEMENT_TAB.HISTORIE) onTabChange(PLACEMENT_TAB.HISTORIE); }} data-testid="aus-primary-pk-klaeren">
          Abweichung klaeren
        </Button>
      );
    }
  } else if (canWrite && p.status === PLACEMENT_STATUS.ABGESCHLOSSEN) {
    // Slice 15: a closed placement whose police is not yet delivered offers the e-mail Zustellung.
    if (ab.data?.aktionen.police_zustellen && canSendMail) {
      primaryAction = (
        <Button onClick={() => { if (activeTab !== PLACEMENT_TAB.HISTORIE) onTabChange(PLACEMENT_TAB.HISTORIE); }} data-testid="aus-primary-pk-zustellen">
          Police zustellen
        </Button>
      );
    }
  }

  async function doAbbrechen() {
    setAbbrechenError(null);
    try {
      await abbrechen.mutateAsync(grund.trim());
      setAbbrechenOpen(false);
      setGrund('');
    } catch (err) {
      setAbbrechenError(err instanceof Error ? err.message : 'Abbrechen fehlgeschlagen.');
    }
  }

  return (
    <div className="flex flex-col gap-0 p-4">
      <DetailShell
        icon={ClipboardList}
        title={<span className="mono-num">{p.placement_number}</span>}
        status={<PlacementStatusBadge status={p.status} />}
        extraBadge={
          <PlacementClosureBadge
            status={p.closure.status}
            startZeitpunkt={p.closure.start_zeitpunkt}
            endZeitpunkt={p.closure.end_zeitpunkt}
            abschlussArt={p.closure.abschluss_art}
          />
        }
        meta={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <EntityLink onActivate={backToKunde}>Kunde öffnen</EntityLink>
            <span aria-hidden>·</span>
            <span>{p.betreuer_name ?? '-'}</span>
            <span aria-hidden>·</span>
            <span>Beginn {p.gewuenschter_beginn ? dateShortDe(p.gewuenschter_beginn) : '-'}</span>
            <span aria-hidden>·</span>
            <span>{PLACEMENT_ANLASS_LABELS[p.anlass]}</span>
            {p.policy_number && (
              <>
                <span aria-hidden>·</span>
                <span className="mono-num">Police {p.policy_number}</span>
              </>
            )}
            {/* Slice-7 carrier-chip slot — the selected Versicherer, visible on every tab; Slice 8
                overlays the submission status/Frist once submissions exist. */}
            <PlacementCarrierChips chips={submissions.data?.chips ?? auswahl.data?.chips ?? []} />
          </span>
        }
        actions={
          canAbbrechen
            ? [
                {
                  label: 'Placement abbrechen',
                  icon: XCircle,
                  destructive: true,
                  onClick: () => setAbbrechenOpen(true),
                },
              ]
            : []
        }
        backTo={{ label: 'Zurück zum Kunden', onClick: backToKunde }}
        primaryAction={primaryAction}
        tabs={tabs}
        primaryCount={6}
        activeTab={activeTab}
        onTabChange={onTabChange}
      >
        {/* Slice 15 / P2 hardening 05: the Policenkontrolle Befund on the Hauptscreen (every tab). */}
        {ab.data?.kontrolle && (() => {
          const pk = ab.data.kontrolle;
          const closureErledigt = ab.data.closure.status === 'erledigt';
          const zugestellt = !!ab.data.zustellung.zugestellt_at;
          if (pk.result === 'abweichung') {
            if (closureErledigt) {
              return (
                <div className="mb-3 rounded-lg border border-status-success/40 bg-status-success/5 p-2 text-sm" data-testid="pk-befund-card" data-result="abweichung">
                  Abweichung geklaert und freigegeben - {pk.befund.headline}
                </div>
              );
            }
            return (
              <div className="mb-3">
                <PolicenkontrolleBefundCard
                  variant="banner"
                  kontrolle={pk}
                  placementNumber={p.placement_number}
                  aktionen={ab.data.aktionen}
                  canWrite={canWrite}
                  canSend={canSendMail}
                  onRueckmeldung={() => setPkRueckmeldenOpen(true)}
                  onKlaeren={() => { if (activeTab !== PLACEMENT_TAB.HISTORIE) onTabChange(PLACEMENT_TAB.HISTORIE); }}
                />
              </div>
            );
          }
          // deckungsgleich: a compact green one-liner, hidden once erledigt + delivered.
          if (closureErledigt && zugestellt) return null;
          return (
            <div className="mb-3 rounded-lg border border-status-success/40 bg-status-success/5 p-2 text-sm" data-testid="pk-befund-card" data-result="deckungsgleich">
              {pk.befund.headline}
              {ab.data.police?.status === 'draft' ? ` - ${PK_WARTET_AKTIVIERUNG_HINWEIS}` : ''}
            </div>
          );
        })()}
        {primaryError && (
          <p role="alert" className="mb-3 text-sm text-status-danger" data-testid="aus-primary-error">
            {primaryError}
          </p>
        )}
        {/* Slices 5 (Tab 1/2) + 7 (Tab 3a) fill their bodies; the other three stay placeholders. */}
        {renderTabBody(
          activeTab,
          p,
          canWrite,
          canSendMail,
          {
            versendenOpen,
            onVersendenClose: () => setVersendenOpen(false),
          },
          {
            openKind: kfOpenKind,
            onOpenHandled: () => setKfOpenKind(null),
          },
          {
            openKind: antragOpenKind,
            onOpenHandled: () => setAntragOpenKind(null),
          },
          {
            openKind: uwOpenKind,
            onOpenHandled: () => setUwOpenKind(null),
            onAntragVorbereiten: () => setAntragOpenKind('vorbereiten'),
          },
        )}
      </DetailShell>

      <Dialog
        open={abbrechenOpen}
        onClose={() => {
          if (!abbrechen.isPending) {
            setAbbrechenOpen(false);
            setAbbrechenError(null);
          }
        }}
        title="Placement abbrechen"
        footer={
          <>
            <Button variant="ghost" onClick={() => setAbbrechenOpen(false)} disabled={abbrechen.isPending}>
              Zurück
            </Button>
            <Button
              variant="destructive"
              onClick={() => void doAbbrechen()}
              disabled={abbrechen.isPending || grund.trim().length < 3}
              data-testid="aus-abbrechen-confirm"
            >
              {abbrechen.isPending ? 'Wird abgebrochen …' : 'Abbrechen bestätigen'}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            Diese Ausschreibung wird abgebrochen. Bitte einen Grund angeben (min. 3 Zeichen).
          </p>
          <textarea
            value={grund}
            onChange={(e) => setGrund(e.target.value)}
            rows={3}
            aria-label="Abbruch-Grund"
            data-testid="aus-abbrechen-grund"
            className="w-full rounded-md border bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {abbrechenError && (
            <p role="alert" className="text-sm text-status-danger" data-testid="aus-abbrechen-error">
              {abbrechenError}
            </p>
          )}
        </div>
      </Dialog>

      {ab.data?.kontrolle && (
        <PkRueckmeldungDialog
          open={pkRueckmeldenOpen}
          onClose={() => setPkRueckmeldenOpen(false)}
          saving={pkRueckmelden.isPending}
          error={pkRueckmelden.error as { code?: string } | null}
          defaultTo={ab.data.rueckmeldung_empfaenger_vorschlag}
          defaultText={buildPkRueckmeldungText({
            versicherer_name: ab.data.antrag?.carrier_name ?? ab.data.police?.carrier_name ?? 'Versicherer',
            kunde_name: 'den Kunden',
            policy_number: ab.data.police?.policy_number ?? ab.data.antrag?.police_erhalten_policy_number ?? '',
            submission_number: ab.data.antrag?.submission_number ?? null,
            befund: ab.data.kontrolle.befund,
          })}
          bereitsGesendet={!!ab.data.kontrolle.rueckmeldung}
          onSend={({ to, text }) => pkRueckmelden.mutate({ to, text }, { onSuccess: () => setPkRueckmeldenOpen(false) })}
        />
      )}
    </div>
  );
}
